import {isValid, differenceInDays, differenceInHours} from 'date-fns'
import {formatInTimeZone, utcToZonedTime} from 'date-fns-tz'

export const ICELAND_TIMEZONE = 'Europe/London'

// eslint-disable-next-line @typescript-eslint/prefer-namespace-keyword, @typescript-eslint/no-namespace
export module DateUtils {
  export function validateDate(date: Date) {
    return isValid(date)
  }

  /* Format date with current user time zone to Iceland timezone with provided date template */
  export function format(date: Date | string, formatTemplate: string) {
    if (!date || (typeof date === 'object' && !DateUtils.validateDate(date))) return ''

    return formatInTimeZone(date, ICELAND_TIMEZONE, formatTemplate)
  }

  /** Get current date in Iceland timezone.
   * Result is Date object in user's timezone with shifter hours to Iceland timezone
   */
  export function current() {
    return utcToZonedTime(new Date().toISOString(), ICELAND_TIMEZONE)
  }

  /** Transforms utc date string to date object in Iceland timezone.
   * Result is Date object in user's timezone with shifter hours to Iceland timezone
   */
  export function utcToZonedDate(utcString: string) {
    return utcToZonedTime(utcString, ICELAND_TIMEZONE)
  }

  export function formatDeliveryDate(startTime: string, endTime: string, dateFormat?: string, convertTo12HourClock?: boolean): [string, string] | null {
    if (!startTime || !endTime) return null

    const format = dateFormat || 'dd MMMM yyyy'

    let timeStr = `${DateUtils.format(startTime, 'HH:mm')} - ${DateUtils.format(endTime, 'HH:mm',)}`
    if (convertTo12HourClock) {
      timeStr = `${formatInTimeZone(startTime, ICELAND_TIMEZONE, "h:mm aa")} - ${formatInTimeZone(endTime, ICELAND_TIMEZONE, "h:mm aa")}`
    }

    const dateStr = DateUtils.format(endTime, format)

    return [timeStr, dateStr]
  }

  /**
   * Tests if a date is before another date
   * @param {Date} firstDate the date we're checking
   * @param {Date} secondDate the date to compare to
   * @returns {boolean} True if first date before second date
   */
  export function isBefore(firstDate: Date, secondDate: Date): boolean | null {
    // Return Null if invalid or not set dates
    if(!firstDate || !DateUtils.validateDate(firstDate) || !secondDate || !DateUtils.validateDate(secondDate)) {
      return null
    } 

    return firstDate.getTime() < secondDate.getTime()
  }

  export function getOrdinal(day:number){
    if (day > 3 && day < 21) return `${day}th`;
    switch (day % 10) {
      case 1: return `${day}st`;
      case 2: return `${day}nd`;
      case 3: return `${day}rd`;
      default: return `${day}th`;
    }
  }

  /**
   * Get 12 hour time format string from date object eg 5:00PM
   * @param {Date} Standard JS date object
   * @returns {string} 12 time string
   */
  export function get12HourTime(date:Date){
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(date).replace(' ', '');
  }

  /** 
   * Get 24 hour time format string from date object eg 14:00
   * @param {Date} date JS date object
   * @returns {string} 24 hour time string
   */
  export function get24HourTime(date:Date){
    return new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(date)
  }

  export function convert12HourTo24Hour(time12h: string) {
    const isPM = time12h.toLowerCase().includes('pm');

    const time = time12h.replace(/[^0-9:]/g, '');
    let [hours, minutes] = time.split(':');

    if (isPM && hours !== '12') {
      hours = (parseInt(hours, 10) + 12).toString();
    }

    if (!isPM && hours === '12') {
      hours = '00';
    }

    if (!isPM && hours.length === 1) {
      hours = '0' + hours;
    }

    return `${hours}:${minutes}`;
  }

  /**
   * Get the day name from a date object eg Monday
   * @param {Date} date Standard JS date object
   * @returns {string} Day name
   */
  export function getDayName(date: Date) {
    return date.toLocaleDateString('en-GB', { weekday: 'long' })
  }

  /**
   * Get the start of the day for a given date
   * @param {Date} date Standard JS date object
   * @returns {Date} Start of the day for the given date
   */
  export function getStartofDay(date: Date) {
    const startOfDay = new Date(date)
    startOfDay.setHours(0, 0, 0, 0)
    return startOfDay
  }

  /**
   * Get the end of the day for a given date
   * @param {Date} date Standard JS date object
   * @returns {Date} End of the day for the given date
   */
  export function getEndofDay(date: Date) {
    const endOfDay = new Date(date)
    endOfDay.setHours(23, 59, 59, 999)
    return endOfDay
  }

  /**
 * Convert a 24h time string (e.g. "14:00") to a 12h time string (e.g. "2:00 PM")
 * @param {string} time24h Time string in 24h format
 * @returns {string} Time string in 12h format
 */
  export const convertTo12h = (time24h: string | undefined) => {
    if (!time24h) return ''
    // Split string into hours and minutes
    const [hourStr, minute] = time24h.split(':')
    let hour = parseInt(hourStr, 10)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    hour = hour % 12
    hour = hour ? hour : 12

    return `${hour}:${minute} ${ampm}`
  }

  export const hoursSinceDate = (date: string | undefined) => {
    if (!date) return null
  
    const dateObj = new Date(date)
    const now = new Date()
    
    const diffInHours = differenceInHours(now, dateObj)
  
    return diffInHours
  }

  export const daysSinceDate = (date: string | undefined) => {
    if (!date) return null
  
    const dateObj = new Date(date)
    const today = new Date()
    
    const diffInDays = differenceInDays(today, dateObj)
  
    return Object.is(diffInDays, -0) ? 0 : diffInDays
  }


}
